import { toast } from "vue-sonner";
import { useI18n } from "vue-i18n";
import { useProModal } from "@/store/useProModal";

type ShortfallInput = {
  /** Feature key for funnel tracking, e.g. "summary". */
  feature: string;
  required?: number | null;
  available?: number | null;
  /** Use a feature-specific message instead of the generic one. */
  message?: string;
  /** Runs before the purchase modal opens, e.g. to close the dialog the error came from. */
  beforeOpen?: () => void;
};

/**
 * "Not enough credits" handling with a way out: the toast carries a
 * "Purchase credits" action that opens the pay-per-use modal, preset to cover
 * the shortfall when the server told us both numbers.
 */
export const useCreditShortfall = () => {
  const { t } = useI18n();
  const proModal = useProModal();
  const { trackUpsell } = useUpsellTracking();

  const hasNumbers = (input: ShortfallInput) =>
    Number.isFinite(Number(input.required)) &&
    Number.isFinite(Number(input.available)) &&
    input.required != null &&
    input.available != null;

  const shortfallOf = (input: ShortfallInput) =>
    hasNumbers(input)
      ? Math.max(0, Number(input.required) - Number(input.available))
      : undefined;

  /** Opens the credit purchase modal for this shortfall. */
  const openBuyCredits = (input: ShortfallInput) => {
    trackUpsell("gate_click", { feature: input.feature, modal: "payPerUse" });
    input.beforeOpen?.();
    proModal.onOpen("payPerUse", {
      feature: input.feature,
      shortfall: shortfallOf(input),
    });
  };

  const notifyInsufficientCredits = (input: ShortfallInput) => {
    trackUpsell("credit_block", { feature: input.feature });
    const message =
      input.message ??
      (hasNumbers(input)
        ? t("errors.notEnoughCreditsForJob", {
            required: input.required,
            available: input.available,
          })
        : t("errors.notEnoughCredits"));

    toast.error(message, {
      duration: 8000,
      description: t("errors.buyMoreCredits"),
      action: {
        label: t("buttons.purchaseCredits"),
        onClick: () => openBuyCredits(input),
      },
    });
  };

  return { notifyInsufficientCredits, openBuyCredits };
};
